import type { ExperienceDefinition, ExperienceContext } from '../types'

interface EvaluatedExperience {
  experience: ExperienceDefinition
  priorityScore: number
}

/**
 * Resolves which experience should run from a list of eligible experiences.
 */
export function resolveWinningExperience(
  experiences: ExperienceDefinition[],
  context: ExperienceContext,
  favorites: string[] = []
): ExperienceDefinition | null {
  if (experiences.length === 0) return null

  // 1. Calculate the dynamic priority score for each candidate
  const evaluated: EvaluatedExperience[] = experiences.map((exp) => {
    const id = exp.metadata.id
    const base = exp.eligibility.basePriority

    let score = base

    // Boost favorite experiences
    if (favorites.includes(id)) {
      score += 100
    }

    // Boost experiences matching the user's specific context (e.g. premium user playing premium category)
    if (exp.metadata.category === 'premium' && context.user.membership === 'PREMIUM') {
      score += 30
    }

    // Fatigue penalty: slightly penalize experiences that have been run many times to avoid repetition
    const historyEntry = context.history[id]
    if (historyEntry) {
      score -= historyEntry.runCount * 1.5
      
      // Additional penalty if it was run recently (e.g. within 2 hours)
      const hrsSinceLastRun = (Date.now() - historyEntry.lastRun) / (1000 * 60 * 60)
      if (hrsSinceLastRun < 2) {
        score -= 20
      }
    }

    return {
      experience: exp,
      priorityScore: Math.max(0, score), // Don't let priority drop below 0
    }
  })

  // 2. Sort candidates by score descending
  evaluated.sort((a, b) => b.priorityScore - a.priorityScore)

  const highestScore = evaluated[0].priorityScore

  // 3. Find all candidates that share the highest score (or are within 0.1 of it)
  const topCandidates = evaluated.filter(
    (item) => Math.abs(item.priorityScore - highestScore) < 0.1
  )

  if (topCandidates.length === 1) {
    return topCandidates[0].experience
  }

  // 4. Perform weighted random selection among the tied candidates
  const totalWeight = topCandidates.reduce((sum, item) => sum + item.priorityScore, 0)
  
  if (totalWeight <= 0) {
    // If all weights are 0, pick a flat random candidate
    const randomIndex = Math.floor(Math.random() * topCandidates.length)
    return topCandidates[randomIndex].experience
  }

  let randomValue = Math.random() * totalWeight
  for (const item of topCandidates) {
    randomValue -= item.priorityScore
    if (randomValue <= 0) {
      return item.experience
    }
  }

  return topCandidates[0].experience
}
